import { type Language } from "@ap/i18n";
import { Button } from "antd";
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { needSourcesFor } from "../lib/needsYou";
import { readPref, writePref } from "../lib/storage";

/** Bump when the steps change meaningfully: everyone sees the new tour once. */
export const TOUR_VERSION = 1;
/** Set to "off" to never start the tour automatically (it stays available from Help). */
export const TOUR_AUTO_KEY = "ap.tour.auto";

export const tourKey = (username: string) => `ap.tour.v${TOUR_VERSION}.${username || "anonymous"}`;

/** True when the first-run tour should start by itself for this user. */
export function shouldAutoStartTour(username: string, store?: Storage): boolean {
  if (readPref(TOUR_AUTO_KEY, store) === "off") return false;
  return readPref(tourKey(username), store) !== "done";
}

export function markTourSeen(username: string, store?: Storage): void {
  writePref(tourKey(username), "done", store);
}

export interface TourStep {
  id: string;
  /** CSS selector of the element to highlight; none = centred card. */
  target?: string;
}

/** The steps for a user's roles on the current page (4–6 steps; missing targets are skipped at run time). */
export function tourSteps(roles: readonly string[], pathname: string): TourStep[] {
  const steps: TourStep[] = [{ id: "welcome" }, { id: "nav", target: '[data-tour="nav"]' }, { id: "palette", target: '[data-tour="palette"]' }];
  if (needSourcesFor(roles).length > 0) steps.push({ id: "bell", target: '[data-tour="bell"]' });
  steps.push({ id: "help", target: '[data-tour="help"]' });
  if (pathname.startsWith("/chat")) steps.push({ id: "agentPicker", target: "main .ap-page-actions .ant-select" });
  else if (!pathname.startsWith("/home")) steps.push({ id: "home", target: '[data-key="home"]' });
  return steps.slice(0, 6);
}

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

function visibleRect(selector: string | undefined): Rect | null {
  if (!selector) return null;
  const el = document.querySelector(selector);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width === 0 && r.height === 0) return null;
  return { top: r.top, left: r.left, width: r.width, height: r.height };
}

/**
 * Guided tour (ADR-0055): a modal card next to the highlighted element. Esc or "Skip" ends it;
 * Back/Next move; focus stays inside the card and returns to where it was afterwards.
 */
export function Tour({ open, steps, lang, onClose }: { open: boolean; steps: TourStep[]; lang: Language; onClose: () => void }) {
  const { t } = useTranslation("tour");
  const [i, setI] = useState(0);
  const [rect, setRect] = useState<Rect | null>(null);
  const card = useRef<HTMLDivElement>(null);
  const returnTo = useRef<HTMLElement | null>(null);
  const uid = useId();
  // Only steps whose element exists (or that need none).
  const [live, setLive] = useState<TourStep[]>([]);

  useEffect(() => {
    if (!open) return;
    returnTo.current = document.activeElement as HTMLElement | null;
    setLive(steps.filter((s) => !s.target || visibleRect(s.target)));
    setI(0);
  }, [open, steps]);

  const step = live[i];
  const measure = useCallback(() => setRect(visibleRect(step?.target)), [step]);
  useLayoutEffect(() => {
    if (!open || !step) return;
    const el = step.target ? document.querySelector(step.target) : null;
    el?.scrollIntoView?.({ block: "nearest" });
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [open, step, measure]);

  useEffect(() => {
    if (open && step) card.current?.focus();
  }, [open, step]);

  const finish = () => {
    onClose();
    returnTo.current?.focus?.();
  };

  if (!open || !step) return null;
  const last = i === live.length - 1;

  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      finish();
      return;
    }
    if (e.key === "Tab") {
      // Keep focus inside the card (modal).
      const f = Array.from(card.current?.querySelectorAll<HTMLElement>("button:not([disabled])") ?? []);
      if (f.length === 0) return;
      const first = f[0] as HTMLElement;
      const end = f[f.length - 1] as HTMLElement;
      if (e.shiftKey && (document.activeElement === first || document.activeElement === card.current)) {
        e.preventDefault();
        end.focus();
      } else if (!e.shiftKey && document.activeElement === end) {
        e.preventDefault();
        first.focus();
      }
    }
  };

  // Card position: below the target when there is room, else above; aligned to its start edge.
  const vw = typeof window !== "undefined" ? window.innerWidth : 1200;
  const vh = typeof window !== "undefined" ? window.innerHeight : 800;
  const W = Math.min(340, vw - 32);
  let pos: CSSProperties = { insetBlock: 0, insetInline: 0, margin: "auto", blockSize: "fit-content" };
  if (rect) {
    const startEdge = lang === "ar" ? vw - (rect.left + rect.width) : rect.left;
    const roomEnd = vw - (startEdge + rect.width);
    if (rect.height > vh * 0.5 && roomEnd > W + 24) {
      // Tall targets (the rail): put the card beside them.
      pos = { insetBlockStart: Math.max(16, Math.min(rect.top + 24, vh - 260)), insetInlineStart: startEdge + rect.width + 16 };
    } else {
      const below = rect.top + rect.height + 12;
      const top = below + 220 < vh ? below : Math.max(16, rect.top - 232);
      pos = { insetBlockStart: top, insetInlineStart: Math.max(16, Math.min(startEdge, vw - W - 16)) };
    }
  }
  const pad = 6;

  return (
    <div className="ap-tour" data-testid="tour">
      {rect ? (
        <div
          className="ap-tour-spot"
          aria-hidden
          style={{ insetBlockStart: rect.top - pad, insetInlineStart: (lang === "ar" ? vw - (rect.left + rect.width) : rect.left) - pad, inlineSize: rect.width + pad * 2, blockSize: rect.height + pad * 2 }}
        />
      ) : (
        <div className="ap-tour-backdrop" aria-hidden />
      )}
      {/* The dialog handles Esc and keeps Tab inside itself (modal). */}
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <div
        ref={card}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${uid}-t`}
        aria-describedby={`${uid}-b`}
        tabIndex={-1}
        className="ap-tour-card"
        style={{ ...pos, inlineSize: W }}
        onKeyDown={onKey}
      >
        <div className="ap-tour-step">{t("stepOf", { n: i + 1, total: live.length })}</div>
        <h2 id={`${uid}-t`} className="ap-tour-title">
          {t(`steps.${step.id}.title`)}
        </h2>
        <p id={`${uid}-b`} className="ap-tour-body">
          {t(`steps.${step.id}.body`)}
        </p>
        <div className="ap-tour-actions">
          <Button type="text" onClick={finish} data-testid="tour-skip">
            {t("skip")}
          </Button>
          <span className="ap-tour-spacer" />
          {i > 0 ? (
            <Button onClick={() => setI(i - 1)} data-testid="tour-back">
              {t("back")}
            </Button>
          ) : null}
          <Button type="primary" onClick={() => (last ? finish() : setI(i + 1))} data-testid="tour-next">
            {last ? t("done") : t("next")}
          </Button>
        </div>
      </div>
    </div>
  );
}
